<template>
    <div id="app" class="p-4 flex flex-col w-full min-h-screen bg-gray-200 items-center">
        <h2
            class="my-2 text-3xl text-center leading-9 tracking-tight font-extrabold text-gray-900 sm:text-4xl sm:leading-10"
        >
            Civil Rights Movement Events in {{ selected }}
        </h2>
        <h2
            class="my-2 text-3xl text-center leading-9 tracking-tight font-extrabold text-gray-900 sm:text-4xl sm:leading-10"
        >
            Jun 9th - Jul 27
        </h2>
        <div class="flex flex-wrap w-full sm:w-2/3">
            <div class="w-full flex p-2">
                <div class="relative w-3/4">
                    <select
                        v-model="selected"
                        @change="updateEvents"
                        class="block appearance-none w-full bg-white border border-gray-500 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                    >
                        <option value="ATL">Atlanta Metro Area</option>
                        <option value="CHI">Chicago Metro Area</option>
                        <option value="DMV">DC Metro Area</option>
                        <option value="BOS">Greater Boston Area</option>
                        <option value="LA">Los Angeles Metro Area</option>
                        <option value="MI">Michigan</option>
                        <option value="NOLA">New Orleans</option>
                        <option value="NYC">NYC Metro Area</option>
                        <option value="SF">San Francisco Bay Area</option>
                    </select>
                    <div
                        class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-700"
                    >
                        <svg
                            class="fill-current h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                        >
                            <path
                                d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                            />
                        </svg>
                    </div>
                </div>
                <button
                    @click="toggleMap"
                    type="button"
                    class="ml-2 w-1/4 text-center items-center px-2 py-1 border border-transparent text-xs leading-4 font-medium rounded text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700 transition ease-in-out duration-150"
                >
                    {{ mapView ? 'List' : 'Map' }}
                </button>
            </div>
        </div>

        <div v-show="!mapView" class="flex flex-wrap w-full sm:w-2/3">
            <div class="loader" v-show="loading">Loading...</div>
            <div
                v-show="!loading"
                v-for="(event, index) in publishedEvents"
                :key="index"
                class="flex p-2 w-full md:w-1/3"
            >
                <div class="w-full bg-white shadow">
                    <div class="relative" style="padding-bottom: 100%">
                        <img
                            class="absolute h-full w-full object-cover "
                            :src="event.gsx$posterimage.$t ? event.gsx$posterimage.$t : defaultImg"
                            alt=""
                        />
                    </div>
                    <div class="p-2 overflow-hidden">
                        <div class="font-semibold flex justify-between text-xs text-gray-600">
                            <div class="flex">
                                <svg
                                    class="mr-1 h-4 w-4"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    ></path>
                                </svg>
                                {{ displayDate(event.gsx$datetimestart.$t) }}
                            </div>
                            <div class="flex">
                                <svg
                                    class="h-4 w-4 min-h-4 min-w-4 mr-1"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                                <time>
                                    {{ displayTime(event.gsx$datetimestart.$t) }}
                                </time>
                                <span class="mx-1" v-if="event.gsx$datetimeend.$t">
                                    -
                                </span>
                                <time v-if="event.gsx$datetimeend.$t">
                                    {{ displayTime(event.gsx$datetimeend.$t) }}
                                </time>
                            </div>
                        </div>
                        <h3 class="mt-2 text-xl leading-7 font-semibold text-gray-900">
                            {{ event.gsx$eventname.$t }}
                        </h3>
                        <div
                            v-if="event.gsx$organizers.$t"
                            class="mt-1 flex items-center text-gray-600 whitespace-normal"
                        >
                            <p class="text-sm overflow-hidden">
                                Organizer: {{ event.gsx$organizers.$t }}
                            </p>
                        </div>
                        <p class="mt-2 text-xs leading-2 text-gray-600">
                            {{ event.gsx$organizertext.$t }}
                        </p>

                        <div class="mt-2 flex items-center text-gray-600 truncate">
                            <svg
                                class="mr-1 h-4 w-4 text-gray-600 min-w-4 min-h-4"
                                fill="none"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                ></path>
                                <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            </svg>
                            <a class="overflow-hidden" :href="event.gsx$locationgeo.$t">{{
                                event.gsx$locationgeo.$t
                            }}</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex flex-wrap w-full sm:w-2/3">
            <Map
                v-if="mapView"
                :mapView="mapView"
                :currentcenter="this.url[this.selected].coords"
                :locations="
                    this.events
                        .map(event => {
                            return [
                                event.id.$t,
                                event.gsx$longitude.$t != ''
                                    ? Number(event.gsx$longitude.$t)
                                    : null,
                                event.gsx$latitude.$t != '' ? Number(event.gsx$latitude.$t) : null,
                            ]
                        })
                        .filter(item => item[1] != null || item[2] != null)
                "
                :events="
                    this.events.filter(
                        event =>
                            event.gsx$latitude.$t != '' &&
                            event.gsx$longitude.$t != '' &&
                            event.gsx$published.$t === 'TRUE'
                    )
                "
                class="w-full p-2"
            ></Map>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import Map from './components/Map'
// https://spreadsheets.google.com/feeds/worksheets/spreadsheetID/public/basic?alt=json
// 1D2BCzytyYLrhPGgDmSwewZjBBnc_vUp_vxR7UGaRDhw
//https://spreadsheets.google.com/feeds/worksheets/1D2BCzytyYLrhPGgDmSwewZjBBnc_vUp_vxR7UGaRDhw/public/basic?alt=json
export default {
    name: 'App',
    components: {
        Map,
    },
    data() {
        return {
            mapView: false,
            zoom: 12,
            center: [-73.95267873759373, 40.71381076434636],
            rotation: 0,
            geolocPosition: undefined,
            selected: 'NYC',
            url: {
                NYC: {
                    url:
                        'https://spreadsheets.google.com/feeds/list/1D2BCzytyYLrhPGgDmSwewZjBBnc_vUp_vxR7UGaRDhw/od6/public/values?alt=json',
                    coords: [-73.95267873759373, 40.71381076434636],
                },
                CHI: {
                    url:
                        'https://spreadsheets.google.com/feeds/list/1D2BCzytyYLrhPGgDmSwewZjBBnc_vUp_vxR7UGaRDhw/o912mma/public/values?alt=json',
                    coords: [-87.630334, 41.87834],
                },
                LA: {
                    url:
                        'https://spreadsheets.google.com/feeds/list/1D2BCzytyYLrhPGgDmSwewZjBBnc_vUp_vxR7UGaRDhw/okzm0qq/public/values?alt=json',
                    coords: [-118.247081, 34.051458],
                },
                SF: {
                    url:
                        'https://spreadsheets.google.com/feeds/list/1D2BCzytyYLrhPGgDmSwewZjBBnc_vUp_vxR7UGaRDhw/omd1890/public/values?alt=json',
                    coords: [-122.397705, 37.78551],
                },
                ATL: {
                    url:
                        'https://spreadsheets.google.com/feeds/list/1D2BCzytyYLrhPGgDmSwewZjBBnc_vUp_vxR7UGaRDhw/odyyj2c/public/values?alt=json',
                    coords: [-84.387891, 33.755519],
                },
                DMV: {
                    url:
                        'https://spreadsheets.google.com/feeds/list/1D2BCzytyYLrhPGgDmSwewZjBBnc_vUp_vxR7UGaRDhw/obijcla/public/values?alt=json',
                    coords: [-77.036993, 38.907125],
                },
                BOS: {
                    url:
                        'https://spreadsheets.google.com/feeds/list/1D2BCzytyYLrhPGgDmSwewZjBBnc_vUp_vxR7UGaRDhw/of7j7ec/public/values?alt=json',
                    coords: [-71.059989, 42.359227],
                },
                NOLA: {
                    url:
                        'https://spreadsheets.google.com/feeds/list/1D2BCzytyYLrhPGgDmSwewZjBBnc_vUp_vxR7UGaRDhw/o55d584/public/values?alt=json',
                    coords: [-90.064212, 29.975694],
                },
                MI: {
                    url:
                        'https://spreadsheets.google.com/feeds/list/1D2BCzytyYLrhPGgDmSwewZjBBnc_vUp_vxR7UGaRDhw/oarcymt/public/values?alt=json',
                    coords: [-83.051196, 42.330142],
                },
            },
            events: [],
            defaultImg: './blm.png',
            loading: true,
        }
    },
    computed: {
        publishedEvents: function() {
            // return this.events.filter(
            //     event =>
            //         event.gsx$published.$t === 'TRUE' && this.futureDate(event.gsx$datetimestart.$t)
            // )
            return this.events
        },
    },
    methods: {
        getData: function() {
            axios.get(this.url[this.selected].url).then(response => {
                this.events = response.data.feed.entry
                this.loading = false
            })
        },
        updateEvents: function() {
            this.loading = true
            axios.get(this.url[this.selected].url).then(response => {
                this.events = response.data.feed.entry
                this.loading = false
            })
        },
        displayDate: function(date) {
            let d = new Date(date.replace(/-/g, '/'))
            let day = d.getDate()
            let month = d.getMonth()
            if (isNaN(day) || isNaN(month)) {
                return '-'
            }
            return month + 1 + '/' + day
        },
        futureDate: function(date) {
            let event = new Date(date.replace(/-/g, '/'))
            let eventDay = event.getDate()
            if (isNaN(eventDay)) {
                return false
            }
            let now = new Date()
            let nowDay = now.getDate()
            console.log({ nowDay })
            return eventDay >= nowDay
        },
        toggleMap: function() {
            this.mapView = !this.mapView
        },
        displayTime: function(date) {
            let d = new Date(date.replace(/-/g, '/'))
            let hours = d.getHours()
            if (hours > 12) {
                hours = hours - 12
            }
            let minutes = d.getMinutes()
            if (minutes < 10) {
                minutes = '0' + minutes
            }
            if (isNaN(hours)) {
                hours = '00'
            }
            if (isNaN(minutes)) {
                minutes = '00'
            }
            return hours + ':' + minutes
        },
    },
    created() {
        this.getData()
    },
}
</script>

<style src="./assets/css/tailwind.css"></style>
<style type="text/css">
.loader,
.loader:before,
.loader:after {
    background: #000;
    -webkit-animation: load1 1s infinite ease-in-out;
    animation: load1 1s infinite ease-in-out;
    width: 1em;
    height: 4em;
}
.loader {
    color: #000;
    text-indent: -9999em;
    margin: 88px auto;
    position: relative;
    font-size: 11px;
    -webkit-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-animation-delay: -0.16s;
    animation-delay: -0.16s;
}
.loader:before,
.loader:after {
    position: absolute;
    top: 0;
    content: '';
}
.loader:before {
    left: -1.5em;
    -webkit-animation-delay: -0.32s;
    animation-delay: -0.32s;
}
.loader:after {
    left: 1.5em;
}
@-webkit-keyframes load1 {
    0%,
    80%,
    100% {
        box-shadow: 0 0;
        height: 4em;
    }
    40% {
        box-shadow: 0 -2em;
        height: 5em;
    }
}
@keyframes load1 {
    0%,
    80%,
    100% {
        box-shadow: 0 0;
        height: 4em;
    }
    40% {
        box-shadow: 0 -2em;
        height: 5em;
    }
}
</style>
